import { inject, Injectable } from '@angular/core';
import { Http, QueryHandlerFor, RootContext, RootContextBase } from '@skylab-angular-shared/core';
import { CurrencyPipe } from '@skylab-angular-shared/pipes';
import { CurrencyCollectionQuery, PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from '@skylab-shared/business';
import { HttpService, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { DdkState, EppState, JpkVatState, KsefReceivedDocumentKind, KsefReceivedDocumentStatus, KsefReceiveSubmissionMode, KsefReceivingSubjectRole, PaymentStatus, PurchaseDocumentKind } from '@skylab-shared/dataModel';
import { Transform } from 'class-transformer';

export interface EInvoiceReceivedGridItem {
    readonly id: number;
    readonly invoiceReferenceNumber: string;
    readonly sellerName: string;
    readonly sellerTin: string;
    readonly ksefNumber: string;
    readonly ksefCollectiveIdentifier: string;
    ksefHash: string;

    readonly issueDate: Date; // KSEF data wystawienia
    readonly dateOfAssigningKsefNumber: Date; //KSEF data nadania numeru

    readonly receivedDate: Date; //EFZ data otrzymania
    readonly vatDeductionDate: Date; //EFZ data otrzymania

    readonly grossValue: string;
    readonly gross: number;
    readonly vatValue: string;
    readonly netValue: string;
    readonly currencySymbol: string;
    readonly xmlUri: string;

    processingState: KsefReceivedDocumentStatus;
    lastXmlCheckDate: Date;

    readonly documentNumber: string;
    readonly documentId: number;
    readonly paymentStatus: PaymentStatus;
    readonly ksefDocumentKind: KsefReceivedDocumentKind;
    readonly ksefSubmissionMode: KsefReceiveSubmissionMode;

    readonly subjectRole: KsefReceivingSubjectRole;

    readonly kind: PurchaseDocumentKind;

    readonly dueDate: Date;
    leftToPay: number;

    readonly ddkState: DdkState;
    readonly eppState: EppState;
    readonly jpkVatState: JpkVatState;
    readonly note: string | null;
}

class EInvoiceReceivedGridItemDTO {
    public readonly id: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly acquisitionTimestamp: Date;
    public readonly invoiceReferenceNumber: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly invoicingDate: Date;
    public readonly kSeFReferenceNumber: string;
    public readonly ksefCollectiveIdentifier: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dateOfAssigningKsefNumber: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly receivedDate: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly vatDeductionDate: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly lastXmlCheckDate: Date;
    public readonly currency: string;
    public readonly net: number;
    public readonly vat: number;
    public readonly gross: number;
    public readonly tenantId: number;
    public readonly leftToPay: number;
    public readonly version: string;
    public readonly status: number;
    public readonly xmlUri: string;
    public readonly ksefHash: string;
    public readonly subjectSellerFullName: string;
    public readonly subjectSellerTin: string;

    public readonly processingState: KsefReceivedDocumentStatus;
    public readonly documentId: number;
    public readonly documentKind: PurchaseDocumentKind;
    public readonly documentNumber: string;
    public readonly ddkState: DdkState;
    public readonly eppState: EppState;
    public readonly jpkVatState: JpkVatState;
    public readonly ksefDocumentKind: KsefReceivedDocumentKind;
    public readonly ksefSubmissionMode: KsefReceiveSubmissionMode;
    public readonly subjectRole: KsefReceivingSubjectRole;
    public readonly note: string | null;
}

export class EInvoicesReceivedCollectionQuery extends PagedCollectionQuery<EInvoiceReceivedGridItem> { }

@Injectable({ providedIn: 'root'})
@QueryHandlerFor(EInvoicesReceivedCollectionQuery)
export class EInvoiceReceivedCollectionQueryHandler {
    #Url = TaggedUrl.join('eInvoices/headers', [Tags.Api]);
    #http = inject(Http) as HttpService;
    #rootContext = inject(RootContext) as any as RootContextBase;
    #currencyPipe = inject(CurrencyPipe);

    public async handleAsync(query: EInvoicesReceivedCollectionQuery): Promise<PagedCollectionQueryResult<EInvoiceReceivedGridItem>> {
        const { data } = await this.#http.get(this.#Url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<EInvoiceReceivedGridItemDTO>(EInvoiceReceivedGridItemDTO)
        });

        return {
            totalItemsCount: data.totalItemsCount,
            items: data.items.map(elem => (<EInvoiceReceivedGridItem>{
                id: elem.id,
                invoiceReferenceNumber: elem.invoiceReferenceNumber,
                netValue: this.parseValue(elem.net, elem.currency),
                vatValue: this.parseValue(elem.vat, elem.currency),
                grossValue: this.parseValue(elem.gross, elem.currency),
                gross: elem.gross,
                ksefNumber: elem.kSeFReferenceNumber,
                ksefCollectiveIdentifier: elem.ksefCollectiveIdentifier,
                sellerName: elem.subjectSellerFullName,
                sellerTin: elem.subjectSellerTin,
                currencySymbol: elem.currency,
                xmlUri: elem.xmlUri,
                processingState: elem.processingState,
                documentId: elem.documentId,
                ksefDocumentKind: elem.ksefDocumentKind,
                dateOfAssigningKsefNumber: elem.dateOfAssigningKsefNumber,
                issueDate: elem.invoicingDate,
                documentNumber: elem.documentNumber,
                jpkVatState: elem.jpkVatState,
                ddkState: elem.ddkState,
                eppState: elem.eppState,

                receivedDate: elem.receivedDate,
                vatDeductionDate: elem.vatDeductionDate,
                dueDate: elem.dueDate,
                leftToPay: elem.leftToPay,
                kind: elem.documentKind,
                ksefHash: elem.ksefHash,
                ksefSubmissionMode: elem.ksefSubmissionMode,
                subjectRole: elem.subjectRole,
                lastXmlCheckDate: elem.lastXmlCheckDate,
                note: elem.note
            }))
        };
    }

    private parseValue(val: number, currencySymbol: string): string {
        if (val == null) return '';
        if (!currencySymbol) return `${val}`;

        const currencies = this.#rootContext.getPreloaded(CurrencyCollectionQuery)!;
        const currency = currencies.find(elem => elem.symbol === currencySymbol);
        if (!currency) return this.#currencyPipe.transform(val, { precision: 2, symbol: currencySymbol });

        return this.#currencyPipe.transform(val, { precision: currency.precision, symbol: currencySymbol });
    }
}
